import forge from "node-forge";

const RSA_KEY_PARAMS = {
  modulusLength: 2048, // Tamanho da chave RSA em bits
  publicExponent: new Uint8Array([1, 0, 1]), // 65537 (valor comum para exponencial pública)
  hash: "SHA-256", // Algoritmo de hash para OAEP
};

// Função para gerar o par de chaves RSA
export const generateRSAKeyPair = async () => {
  try {
    return await crypto.subtle.generateKey(
      {
        name: "RSA-OAEP",
        modulusLength: RSA_KEY_PARAMS.modulusLength,
        publicExponent: RSA_KEY_PARAMS.publicExponent,
        hash: RSA_KEY_PARAMS.hash,
      },
      true, // Chaves exportáveis
      ["encrypt", "decrypt"]
    );
  } catch (error) {
    throw new Error("Erro ao gerar o par de chaves RSA: " + error);
  }
};

// Função para gerar apenas a chave privada
export const generatePrivateKey = (): string => {
  const { privateKey } = forge.pki.rsa.generateKeyPair(2048);
  return forge.pki.privateKeyToPem(privateKey);
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  return window.btoa(String.fromCharCode(...new Uint8Array(buffer)));
};

// Função para criptografar mensagem com a chave pública do destinatário
// export function encryptMessage(message: any , publicKeyPem: any) {
//   const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
//   const encrypted = publicKey.encrypt(message, 'RSA-OAEP');
//   return forge.util.encode64(encrypted); // Convertendo para base64
// }
// Criptografar mensagem usando AES + RSA
export const encryptMessage = async (message: string, publicKey: CryptoKey) => {
  try {
    const iv: Uint8Array = crypto.getRandomValues(new Uint8Array(12));
    const aesKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
    const encodedMessage = new TextEncoder().encode(message);
    const encryptedMessage = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, encodedMessage);

    // Exportar a chave AES como ArrayBuffer
    const exportedKey = await crypto.subtle.exportKey("raw", aesKey);

    // Criptografar a chave AES usando RSA-OAEP
    const encryptedKey = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, exportedKey);

    const buffer: ArrayBuffer =
      iv.buffer instanceof ArrayBuffer ? iv.buffer : (iv.buffer.slice(0) as unknown as ArrayBuffer);

    // Agora buffer é garantidamente ArrayBuffer
    return {
      encryptedMessage: arrayBufferToBase64(encryptedMessage),
      encryptedKey: arrayBufferToBase64(encryptedKey),
      iv: arrayBufferToBase64(buffer),
    };
  } catch (error) {
    throw new Error("Erro ao criptografar a mensagem: " + error);
  }
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  try {
    // Remove possíveis caracteres de formatação
    const cleanBase64 = base64.replace(/-/g, "+").replace(/_/g, "/");
    const binaryString = atob(cleanBase64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (error) {
    console.log("error", error);
    throw new Error(`Base64 conversion failed: ${error}`);
  }
};

// Função para descriptografar mensagem com a chave privada do destinatário
export const decryptMessage = async (
  encryptedData: { encryptedMessage: string; encryptedKey: string; iv: string },
  privateKey: CryptoKey
) => {
  try {
    const { encryptedMessage, encryptedKey, iv } = encryptedData;

    console.log("descript", privateKey, encryptedMessage, encryptedKey, iv);

    // Conversão de Base64 para ArrayBuffer
    const decryptedKey = await crypto.subtle.decrypt(
      { name: "RSA-OAEP" },
      privateKey,
      base64ToArrayBuffer(encryptedKey)
    );

    console.log("descryptedKey", decryptedKey);
    const aesKey = await crypto.subtle.importKey("raw", decryptedKey, { name: "AES-GCM" }, false, ["decrypt"]);

    console.log("aeskey", aesKey);

    // 3. Verificar e preparar o IV
    const ivBuffer = base64ToArrayBuffer(iv);
    if (ivBuffer.byteLength !== 12) {
      throw new Error("IV must be exactly 12 bytes for AES-GCM");
    }
    const decryptedMessage = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(base64ToArrayBuffer(iv)) },
      aesKey,
      base64ToArrayBuffer(encryptedMessage)
    );

    console.log("decryptedMessage", decryptedMessage);

    // Decodificar o ArrayBuffer para string
    return new TextDecoder().decode(decryptedMessage);
  } catch (error) {
    console.log("error", error);
    throw new Error(`Erro ao descriptografar a mensagem: ${error}`);
  }
};

export const decryptMessageOwner = async (encryptedMessage: string, iv: string, key: CryptoKey) => {
  // Descriptografar a mensagem com AES
  try {
    const decryptedMessage = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(base64ToArrayBuffer(iv)) },
      key,
      base64ToArrayBuffer(encryptedMessage)
    );

    // Decodificar o ArrayBuffer para string
    return new TextDecoder().decode(decryptedMessage);
  } catch (error) {
    console.log(error);
    return "";
  }
};

export const generateKeyGroup = async () => {
  try {
    const aesKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);

    return aesKey;
  } catch (error) {
    console.log(error);
  }
};

export const encryptGroupMessage = async (message: string, key: CryptoKey) => {
  const iv: Uint8Array = crypto.getRandomValues(new Uint8Array(12));
  const encodedMessage = new TextEncoder().encode(message);
  const encryptedMessage = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encodedMessage);

  const buffer: ArrayBuffer =
    iv.buffer instanceof ArrayBuffer ? iv.buffer : (iv.buffer.slice(0) as unknown as ArrayBuffer);

  return {
    encryptedMessage: arrayBufferToBase64(encryptedMessage),
    iv: arrayBufferToBase64(buffer),
  };
};

export const decryptGroupMessage = async (encryptedMessage: string, iv: string, key: CryptoKey) => {
  try {
    const decryptedMessage = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: new Uint8Array(base64ToArrayBuffer(iv)) },
      key,
      base64ToArrayBuffer(encryptedMessage)
    );

    // Decodificar o ArrayBuffer para string
    return new TextDecoder().decode(decryptedMessage);
  } catch (error) {
    console.log(error);
    return "";
  }
};

// export const generateAndSendKeys = async (userId: string, setIsLoading: React.Dispatch<React.SetStateAction<boolean>>, setIsSuccess: React.Dispatch<React.SetStateAction<boolean>>, setIsError: React.Dispatch<React.SetStateAction<boolean>>, setShowKeyModal: React.Dispatch<React.SetStateAction<boolean>>) => {
//   try {
//     console.log("Iniciando a geração das chaves...");
//     setIsLoading(true);

//     // Gerando as chaves RSA
//     const { publicKeyPem, privateKeyPem } = generateRSAKeyPair(); // Supondo que a função generateRSAKeyPair já esteja importada

//     console.log("Chaves geradas:", { publicKeyPem, privateKeyPem });

//     // Armazenando a chave privada no localStorage
//     console.log("Armazenando a chave privada...");
//     localStorage.setItem("privateKey", privateKeyPem);

//     // Enviando a chave pública para o backend
//     await sendPublicKeyToBackend(publicKeyPem, userId);

//     setIsSuccess(true);
//     console.log("Chave pública enviada com sucesso!");

//     // Fechar o modal após 5 segundos
//     setTimeout(() => {
//       setShowKeyModal(false);
//     }, 5000);
//   } catch (error) {
//     setIsError(true); // Indica que ocorreu um erro
//     console.error("Erro ao gerar e enviar as chaves:", error);
//     setIsLoading(false);
//   }
// };

// const token = localStorage.getItem("@backoffice:token")
// const sendPublicKeyToBackend = async (publicKeyPem: string, userId: string) => {
//   try {
//     console.log("Enviando chave pública para o backend...");
//     const response = await api.patch(
//       `/graphic/${userId}/update-publickey`,
//       { publicKey: publicKeyPem }, // Payload com a chave pública
//       {
//         headers: {
//           Authorization: `Bearer ${token}`, // Token JWT
//         },
//       }
//     );

//     console.log("Resposta do backend:", response.data);
//   } catch (error) {
//     console.error("Erro ao enviar a chave pública:", error);
//   }
// }

// async function testCrypto() {
//   // Mensagem de teste
//   const message = 'Mensagem secreta para testar a criptografia e descriptografia. V2';

//   // Gerar chaves RSA para o teste (substitua por suas chaves se já tiver)
//   const { publicKeyPem: publicKey, privateKeyPem: privateKey } = generateRSAKeyPair(); // Caso queira testar com chaves novas
//   console.log('Chave pública gerada:');
//   console.log(publicKey);
//   console.log('Chave privada gerada:');
//   console.log(privateKey);

//   // Criptografar a mensagem
//   const encryptedMessage = encryptMessage(message, publicKey);
//   console.log('Mensagem criptografada:');
//   console.log(encryptedMessage);

//   // Descriptografar a mensagem
//   const decryptedMessage = decryptMessage(encryptedMessage, privateKey);
//   console.log('Mensagem descriptografada:');
//   console.log(decryptedMessage);
// }

// testCrypto().catch(console.error);

// node --loader ts-node/esm src/feature/pages/homepage/utils/crypto.ts
